import { InputProps as ArcoInputProps, Select as ArcoSelect } from '@arco-design/web-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from './Input';

export interface WidthInputWithUnitProps extends Omit<ArcoInputProps, 'onChange'> {
  value: string;
  onChange: (val: string) => void;
  unitOptions?: Array<{ value: string; label: string }> | 'default' | 'percent';
  defaultUnit?: string;
  quickchange?: boolean;
}

const defaultUnitOptions = [
  {
    value: 'px',
    label: 'px',
  },
];

const percentUnitOptions = [
  {
    value: 'px',
    label: 'px',
  },
  {
    value: '%',
    label: '%',
  },
];

export function WidthInputWithUnit(props: WidthInputWithUnitProps) {
  const {
    value = '',
    onChange,
    unitOptions: propsUnitOptions,
    defaultUnit: propsDefaultUnit,
    ...restProps
  } = props;

  const resolvedUnitOptions = useMemo(() => {
    if (Array.isArray(propsUnitOptions)) {
      return propsUnitOptions;
    }

    if (propsUnitOptions === 'percent') {
      return percentUnitOptions;
    }

    if (propsUnitOptions === 'default') {
      return defaultUnitOptions;
    }

    return [] as Array<{ value: string; label: string }>;
  }, [propsUnitOptions]);

  const defaultUnit = useMemo(
    () => propsDefaultUnit || resolvedUnitOptions[0]?.value || 'px',
    [propsDefaultUnit, resolvedUnitOptions],
  );

  const parsedValue = useMemo(() => {
    const rawValue = String(value || '').trim();
    const match = rawValue.match(/^(-?\d+(?:\.\d+)?)(px|%)$/i);

    if (match) {
      return {
        numericValue: match[1],
        unit: match[2].toLowerCase(),
      };
    }

    return {
      numericValue: null as string | null,
      unit: null as string | null,
    };
  }, [value]);

  const [selectedUnit, setSelectedUnit] = useState(parsedValue.unit || defaultUnit);

  useEffect(() => {
    if (parsedValue.unit) {
      setSelectedUnit(parsedValue.unit);
      return;
    }

    if (!resolvedUnitOptions.some(option => option.value === selectedUnit)) {
      setSelectedUnit(defaultUnit);
    }
  }, [defaultUnit, parsedValue.unit, resolvedUnitOptions, selectedUnit]);

  const showUnitSelector = resolvedUnitOptions.length > 0;

  const displayValue = parsedValue.numericValue ?? String(value || '');

  const onInputChange = useCallback(
    (nextValue: string) => {
      if (!showUnitSelector) {
        onChange(nextValue);
        return;
      }

      const trimmedValue = String(nextValue || '').trim();

      if (!trimmedValue) {
        onChange('');
        return;
      }

      if (/^-?\d+(?:\.\d+)?$/.test(trimmedValue)) {
        onChange(`${trimmedValue}${selectedUnit}`);
        return;
      }

      onChange(nextValue);
    },
    [onChange, selectedUnit, showUnitSelector],
  );

  const onUnitChange = useCallback(
    (nextUnit: string) => {
      setSelectedUnit(nextUnit);

      const rawValue = String(value || '').trim();
      if (!rawValue) {
        return;
      }

      const numericMatch = rawValue.match(/^(-?\d+(?:\.\d+)?)(?:px|%)?$/i);
      if (numericMatch?.[1]) {
        onChange(`${numericMatch[1]}${nextUnit}`);
      }
    },
    [onChange, value],
  );

  return (
    <Input
      className='width-input-with-unit'
      value={displayValue}
      onChange={onInputChange}
      {...restProps}
      quickchange
      addAfter={
        showUnitSelector ? (
          <ArcoSelect
            value={selectedUnit}
            showSearch={false}
            onChange={nextValue => onUnitChange(String(nextValue))}
            onBlur={restProps.onBlur as any}
            getPopupContainer={() => document.body}
            triggerProps={{
              position: 'bl',
              autoFitPosition: true,
              autoFitTransformOrigin: true,
              updateOnScroll: true,
            }}
            style={{
              width: 72,
              height: '100%',
            }}
          >
            {resolvedUnitOptions.map(option => (
              <ArcoSelect.Option
                key={option.value}
                value={option.value}
              >
                {typeof option.label === 'string' ? option.label : option.value}
              </ArcoSelect.Option>
            ))}
          </ArcoSelect>
        ) : undefined
      }
    />
  );
}
